/**
 * Inventário do hardware medido, com seu consumo.
 *
 * Tudo **detectado, nunca digitado**. Hardware descrito à mão contamina toda a
 * extrapolação que dele deriva, e o erro é invisível: o número parece o mesmo.
 * Campo que não pôde ser detectado aparece como não detectado — nunca
 * preenchido por estimativa, nem omitido em silêncio.
 */

import { useMemo } from "react";
import { motion } from "motion/react";
import { DataTable, type Column } from "../components/DataTable.js";
import { DefinitionList, type Definition } from "../components/DefinitionList.js";
import { Notice } from "../components/Notice.js";
import { Page } from "../components/Page.js";
import { Panel } from "../components/Panel.js";
import { Stat } from "../components/Stat.js";
import { snapshot } from "../data/source.js";
import { MODEL_WEIGHTS_MB } from "../fixtures/scale.js";
import { formatNumber } from "../lib/format.js";

/** Cenário cuja telemetria alimenta esta tela. */
const HARDWARE_SCENARIO_ID = 3;

/** Utilização acima da qual o acelerador é destacado: sem folga para pico de carga. */
const UTIL_ALERT_PCT = 95;

/** Temperatura acima da qual o acelerador é destacado: é onde o throttle costuma começar. */
const TEMP_ALERT_C = 80;

/** Uma amostra de telemetria de acelerador. */
type GpuMetric = (typeof snapshot.gpuMetrics)[number];

/** Marca visual para campo que a detecção não conseguiu preencher. */
function NotDetected(): React.ReactElement {
  return <span className="text-kumo-subtle">não detectado</span>;
}

/** Entradas da barra de uso. */
interface UsageBarProps {
  readonly pct: number;
  readonly alert: boolean;
  readonly label: string;
}

/** Barra de uso que cresce até o valor, vermelha quando passa do limite. */
function UsageBar({ pct, alert, label }: UsageBarProps): React.ReactElement {
  const clamped = Math.min(100, Math.max(0, pct));
  return (
    <div
      className="h-2 w-full min-w-24 overflow-hidden rounded-full bg-kumo-recessed"
      role="meter"
      aria-label={label}
      aria-valuenow={Math.round(clamped)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <motion.div
        className={`h-full rounded-full ${alert ? "bg-kumo-danger" : "bg-kumo-brand"}`}
        initial={{ width: 0 }}
        animate={{ width: `${clamped}%` }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      />
    </div>
  );
}

/** Inventário detectado, consumo elétrico e eficiência de banda. */
export function Hardware(): React.ReactElement {
  const hw = snapshot.hardware;

  const metrics = useMemo(() => snapshot.gpuMetrics.filter((m) => m.scenarioId === HARDWARE_SCENARIO_ID), []);

  /** Estado corrente por acelerador, a partir da última amostra de cada um. */
  const perGpu = useMemo(() => {
    const byIdx = new Map<number, GpuMetric>();
    for (const metric of metrics) {
      byIdx.set(metric.gpuIdx, metric);
    }
    return [...byIdx.values()].sort((a, b) => a.gpuIdx - b.gpuIdx);
  }, [metrics]);

  const throttled = metrics.filter((m) => m.throttleReason !== null);
  const memTotalMb = hw.gpuMemoryGbEach * 1024;
  const bandwidthTotal = hw.gpuBandwidthTbsEach * hw.gpuCount;

  const inventory: readonly Definition[] = [
    { label: "instância", value: hw.instanceType },
    { label: "zona", value: hw.availabilityZone },
    { label: "aceleradores", value: `${hw.gpuCount}× ${hw.gpuModel}` },
    { label: "memória por acelerador", value: `${hw.gpuMemoryGbEach} GB` },
    { label: "memória agregada", value: `${hw.gpuMemoryGbEach * hw.gpuCount} GB` },
    { label: "banda agregada", value: `${formatNumber(bandwidthTotal, 2)} TB/s` },
    {
      label: "interconexão dedicada",
      value: hw.nvlink ? "presente" : <span className="text-kumo-warning">ausente — limita o paralelismo entre aceleradores</span>,
    },
    { label: "processador", value: hw.cpuModel },
    { label: "núcleos", value: hw.cpuCores },
    { label: "memória de sistema", value: `${hw.ramGb} GB` },
    // Métrica com histórico de indisponibilidade no exportador; quando não vier, é
    // declarada como não medida em vez de estimada.
    { label: "tráfego de interconexão", value: <NotDetected /> },
    { label: "pesos do modelo", value: `${formatNumber(MODEL_WEIGHTS_MB / 1024)} GB em FP8` },
  ];

  /** Colunas do estado por acelerador; dependem da memória total para a barra. */
  const columns: readonly Column<GpuMetric>[] = [
    { key: "idx", title: "#", render: (row) => row.gpuIdx },
    {
      key: "util",
      title: "utilização",
      render: (row) => (
        <div className="flex min-w-36 items-center gap-3">
          <UsageBar pct={row.utilPct} alert={row.utilPct > UTIL_ALERT_PCT} label={`utilização do acelerador ${row.gpuIdx}`} />
          <span className="w-10 text-right tabular-nums">{formatNumber(row.utilPct)}%</span>
        </div>
      ),
    },
    {
      key: "mem",
      title: "memória",
      render: (row) => (
        <div className="flex min-w-36 flex-col gap-1.5">
          <span className="whitespace-nowrap">
            {formatNumber(row.memUsedMb / 1024, 1)} / {hw.gpuMemoryGbEach} GB
          </span>
          <UsageBar pct={(row.memUsedMb / memTotalMb) * 100} alert={false} label={`memória do acelerador ${row.gpuIdx}`} />
        </div>
      ),
    },
    { key: "power", title: "potência", align: "right", render: (row) => `${formatNumber(row.powerW)} W` },
    {
      key: "temp",
      title: "temperatura",
      align: "right",
      render: (row) => (
        <span className={row.tempC > TEMP_ALERT_C ? "font-semibold text-kumo-danger" : ""}>
          {formatNumber(row.tempC)} °C
        </span>
      ),
    },
    {
      key: "clock",
      title: "relógio",
      align: "right",
      render: (row) => (
        <span className={`whitespace-nowrap ${row.throttleReason !== null ? "font-semibold text-kumo-danger" : ""}`}>
          {formatNumber(row.smClockMhz)} MHz
        </span>
      ),
    },
    {
      key: "throttle",
      title: "throttle",
      render: (row) =>
        row.throttleReason === null ? (
          <span className="text-kumo-subtle">—</span>
        ) : (
          <span className="text-kumo-danger">{row.throttleReason}</span>
        ),
    },
  ];

  return (
    <Page>
      {throttled.length > 0 && (
        <Notice
          tone="error"
          title="Throttle detectado — esta execução não é comparável"
          description={`${new Set(throttled.map((m) => m.gpuIdx)).size} de ${hw.gpuCount} aceleradores reduziram o relógio durante a medição.`}
        />
      )}

      <Panel
        title="Inventário detectado"
        description="Nenhum campo aceita entrada manual. O que a detecção não alcança aparece como não detectado."
      >
        <DefinitionList items={inventory} columns={3} />
      </Panel>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
        <Panel>
          <Stat
            label="potência instantânea"
            value={hw.powerWTotal}
            suffix="W"
            caption={`soma dos ${hw.gpuCount} aceleradores`}
          />
        </Panel>
        <Panel>
          <Stat
            label="energia da execução"
            value={hw.energyKwhRun}
            precision={3}
            suffix="kWh"
            caption="integral da potência no tempo"
          />
        </Panel>
        <Panel>
          <Stat
            label="eficiência de banda"
            value={hw.bandwidthEfficiencyPct}
            precision={1}
            suffix="%"
            caption="é o que permite extrapolar"
            tone="success"
          />
        </Panel>
        <Panel>
          <Stat
            label="throttle"
            value={throttled.length > 0 ? "detectado" : "ausente"}
            caption={throttled.length > 0 ? "invalida a comparação" : "relógios estáveis"}
            tone={throttled.length > 0 ? "danger" : "success"}
          />
        </Panel>
      </div>

      <Panel title="Estado por acelerador">
        <DataTable columns={columns} rows={perGpu} rowKey={(row) => row.gpuIdx} />
      </Panel>
    </Page>
  );
}
